import * as React from 'react';
import { tabsDriverFactory } from './Tabs.driver';
import { Tabs, TabsSkin, TabsAlignment, TabsVariant } from '.';
import {
  TPAComponentsWrapper,
  createRendererWithUniDriver,
} from '../../test/utils';
import userEvent from '@testing-library/user-event';
import { syntheticEvent } from '../../test/syntheticEvent';

describe('Tabs', () => {
  const render = createRendererWithUniDriver(tabsDriverFactory);
  const items = [
    { title: 'some-title-1' },
    { title: 'some-title-2' },
    { title: 'some-title-3' },
    { title: 'some-title-4' },
    { title: 'some-title-5' },
  ];

  it('should render list of items with titles', async () => {
    const { driver } = render(<Tabs items={items} activeTabIndex={1} />);

    const expectedTitles = items.map((item) => item.title);
    const receivedTitles = await Promise.all(
      Array(items.length)
        .fill(0)
        .map((_, index) => driver.getTitleAt(index)),
    );

    expect(receivedTitles).toEqual(expectedTitles);
  });

  it('should call onTabClick callback when clicking on tab item', async () => {
    const onTabClick = jest.fn();
    const currentSelectedIndex = 0;
    const newSelectedIndex = 1;
    const { driver } = render(
      <Tabs
        items={items}
        activeTabIndex={currentSelectedIndex}
        onTabClick={onTabClick}
      />,
    );
    await driver.clickTabAt(newSelectedIndex);
    expect(onTabClick).toHaveBeenCalled();
    const [selectedIndex, event] = onTabClick.mock.calls[0];

    expect(selectedIndex).toBe(newSelectedIndex);
    expect(event).toEqual(syntheticEvent());
  });

  it('should call onTabClick callback when clicking on selected tab item', async () => {
    const onTabClick = jest.fn();
    const selectedIndex = 0;
    const { driver } = render(
      <Tabs
        items={items}
        activeTabIndex={selectedIndex}
        onTabClick={onTabClick}
      />,
    );
    await driver.clickTabAt(selectedIndex);

    expect(onTabClick).toHaveBeenCalled();
  });

  it('should mark active tab', async () => {
    const selectedIndex = 1;
    const { driver } = render(
      <Tabs items={items} activeTabIndex={selectedIndex} />,
    );

    expect(await driver.getActiveTabIndex()).toBe(selectedIndex);
  });

  it('should set default states stylable states', async () => {
    const { driver } = render(<Tabs items={items} activeTabIndex={0} />);

    expect(await driver.getSkin()).toBe('fullUnderline');
    expect(await driver.getVariant()).toBe('fit');
    expect(await driver.getAlignment()).toBe('center');
  });

  it('should have divider when skin prop is "fullUnderline"', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} skin={TabsSkin.fullUnderline} />,
    );

    expect(await driver.getSkin()).toBe('fullUnderline');
  });

  it('should not have divider when skin prop is "clear"', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} skin={TabsSkin.clear} />,
    );

    expect(await driver.getSkin()).toBe('clear');
  });

  it('should have divider that fits tab width when skin prop is "fitUnderline"', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} skin={TabsSkin.fitUnderline} />,
    );

    expect(await driver.getSkin()).toBe('fitUnderline');
  });

  it('should align content to left when alignment prop is "left"', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} alignment={TabsAlignment.left} />,
    );

    expect(await driver.getAlignment()).toBe('left');
  });

  it('should align content to right when alignment prop is "right"', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} alignment={TabsAlignment.right} />,
    );

    expect(await driver.getAlignment()).toBe('right');
  });

  it('should align content to center when alignment prop is "center"', async () => {
    const { driver } = render(
      <Tabs
        items={items}
        activeTabIndex={0}
        alignment={TabsAlignment.center}
      />,
    );

    expect(await driver.getAlignment()).toBe('center');
  });

  it('should set tabs on all content width', async () => {
    const { driver } = render(
      <Tabs items={items} activeTabIndex={0} variant={TabsVariant.fullWidth} />,
    );

    expect(await driver.getVariant()).toBe('fullWidth');
  });

  it('should use mobile design', async () => {
    const { driver } = render(
      TPAComponentsWrapper({ mobile: true })(
        <Tabs items={items} activeTabIndex={0} data-hook="tabs" />,
      ),
      'tabs',
    );

    expect(await driver.isMobile()).toEqual(true);
  });

  describe('isTablist', () => {
    it('should focus first tab if no activeTabIndex is provided', async () => {
      const user = userEvent.setup();
      render(
        TPAComponentsWrapper({ mobile: true })(
          <div>
            <button>button</button>
            <Tabs items={items} isTablist data-hook="tabs" />
          </div>,
        ),
        'tabs',
      );

      document.querySelector('button').focus();
      await user.tab();

      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'tab-item-0',
      );
    });
    it('should focus activeTabIndex tab', async () => {
      const user = userEvent.setup();
      render(
        TPAComponentsWrapper({ mobile: true })(
          <div>
            <button>button</button>
            <Tabs items={items} activeTabIndex={3} isTablist data-hook="tabs" />
          </div>,
        ),
        'tabs',
      );

      document.querySelector('button').focus();
      await user.tab();

      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'tab-item-3',
      );
    });
    it('should focus previous component on shift tab', async () => {
      const user = userEvent.setup();
      render(
        TPAComponentsWrapper({ mobile: true })(
          <div>
            <button data-hook="my-button">button</button>
            <Tabs items={items} activeTabIndex={3} isTablist data-hook="tabs" />
          </div>,
        ),
        'tabs',
      );

      document.querySelector('button').focus();
      await user.tab();
      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'tab-item-3',
      );

      await user.tab({ shift: true });
      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'my-button',
      );
    });
    it('should focus next component on tab', async () => {
      const user = userEvent.setup();
      render(
        TPAComponentsWrapper({ mobile: true })(
          <div>
            <button data-hook="my-button">button</button>
            <Tabs items={items} activeTabIndex={3} isTablist data-hook="tabs" />
            <button data-hook="my-button-2">button</button>
          </div>,
        ),
        'tabs',
      );

      document.querySelector('button').focus();
      await user.tab();
      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'tab-item-3',
      );

      await user.tab();
      expect(document.activeElement.getAttribute('data-hook')).toBe(
        'my-button-2',
      );
    });
  });
});
